import { CLOUD_MODES, formatCidr, parseCidr, validateTree } from '@it-tools/core-net'
import type { CloudMode } from '@it-tools/core-net'
import { createDesign, type Design } from './design'

/**
 * Share links.
 *
 * Phase 1 keeps the whole design in the URL fragment (`#…`) so nothing is ever sent
 * to a server — the fragment is not transmitted in HTTP requests. The payload is
 * versioned so a future, more compact encoding can be introduced without breaking
 * links people have already shared.
 *
 * v1 is intentionally the simple, readable encoding:
 *   #v1:<root>;<mode>;<leafPrefixes>;<notes>
 *
 * TODO: the reference project's NOTES.md (../../../visualsubnetcalc/NOTES.md)
 * describes a far denser "Nth subnet of size N" scheme worth adopting as v2 once
 * the UI settles. Do NOT change v1's meaning — add v2 and keep reading v1 forever.
 */
const VERSION = 'v1'

export interface SharedState {
  readonly design: Design
  readonly cloudMode: CloudMode
}

export function encodeState(state: SharedState): string {
  const { tree, notes } = state.design
  const prefixes = tree.leaves.map((leaf) => leaf.prefix).join(',')
  const annotations = Object.entries(notes)
    .map(([cidr, note]) => `${cidr}=${note.color}=${encodeURIComponent(note.label)}`)
    .join('|')
  return [VERSION, formatCidr(tree.root), state.cloudMode, prefixes, annotations].join(';')
}

/** Parses a fragment payload. Returns `null` for anything unrecognised — a bad link
 * must land the user on a working default calculator, never on an error page. */
export function decodeState(payload: string): SharedState | null {
  const text = payload.replace(/^#/, '')
  if (text === '') return null

  const [version, rootText, modeText, prefixText, annotationText] = text.split(';')
  if (version !== VERSION || !rootText) return null

  try {
    let design = createDesign(parseCidr(rootText))
    const prefixes = (prefixText ?? '').split(',').filter(Boolean).map(Number)
    if (prefixes.some((prefix) => !Number.isInteger(prefix) || prefix < 0 || prefix > 32)) {
      return null
    }
    design = { tree: rebuildTree(design, prefixes), notes: design.notes }
    validateTree(design.tree)

    for (const entry of (annotationText ?? '').split('|').filter(Boolean)) {
      const [cidr, color, label] = entry.split('=')
      if (!cidr) continue
      design = {
        tree: design.tree,
        notes: {
          ...design.notes,
          [formatCidr(parseCidr(cidr))]: {
            color: color ?? 'none',
            label: decodeURIComponent(label ?? ''),
          },
        },
      }
    }

    // Derived from the registry, never a second hardcoded list — a new provider
    // must not silently fall back to standard when someone opens a shared link.
    const cloudMode = CLOUD_MODES.find((mode) => mode === modeText)
    return { design, cloudMode: cloudMode ?? 'standard' }
  } catch {
    return null
  }
}

/**
 * Rebuilds the leaf list from a flat prefix sequence by walking the address space —
 * the prefixes alone determine the division, since leaves must tile the root in
 * order.
 */
function rebuildTree(design: Design, prefixes: number[]): Design['tree'] {
  if (prefixes.length === 0) return design.tree
  const root = design.tree.root
  let address = root.address
  const leaves = prefixes.map((prefix) => {
    const leaf = { address, prefix }
    address += 2 ** (32 - prefix)
    return leaf
  })
  return { root, leaves }
}

/** Writes the state into the fragment without adding a history entry per keystroke. */
export function replaceLocationState(state: SharedState): void {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}${window.location.search}#${encodeState(state)}`
  window.history.replaceState(null, '', next)
}

export function readLocationState(): SharedState | null {
  if (typeof window === 'undefined') return null
  return decodeState(window.location.hash)
}
